import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Progress, Tooltip, message as antMessage } from 'antd';
import { PaperClipOutlined, SendOutlined } from '@ant-design/icons';
import { SupportUploadLimits, validateFiles } from '../../services/supportApi';
import { useRegisterAttachTarget } from './attach';
import { formatBytes } from './format';
import * as S from './styles';

export interface ComposerProps {
  limits: SupportUploadLimits;
  disabled?: boolean;
  placeholder?: string;
  /** Called with the trimmed text and the chosen files; resolve to clear the composer. */
  onSend: (body: string, files: File[], onProgress: (p: number) => void) => Promise<unknown>;
  autoFocus?: boolean;
}

/**
 * Text + attachments + previews. Enter sends, Shift+Enter breaks a line. Files arrive three ways:
 * the paperclip, a paste, or a drop anywhere on the panel (the panel routes those here).
 */
const Composer: React.FC<ComposerProps> = ({
  limits,
  disabled,
  placeholder,
  onSend,
  autoFocus,
}) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const previews = useMemo(
    () => files.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [files],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;
      const problem = validateFiles(files, incoming, limits);
      if (problem) {
        antMessage.warning(problem);
        return;
      }
      setFiles((prev) => [...prev, ...incoming]);
    },
    [files, limits],
  );

  const openPicker = useCallback(() => inputRef.current?.click(), []);

  // Take over the panel's drop target while this composer is on screen.
  useRegisterAttachTarget({ addFiles, openPicker });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const pasted = Array.from(e.clipboardData?.files ?? []);
    if (pasted.length > 0) {
      e.preventDefault();
      addFiles(pasted);
    }
  };

  const canSend = !disabled && !sending && (text.trim().length > 0 || files.length > 0);

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setProgress(0);
    try {
      await onSend(text.trim(), files, setProgress);
      setText('');
      setFiles([]);
    } catch (e: any) {
      antMessage.error(e?.response?.data?.message || 'Could not send. Please try again.');
    } finally {
      setSending(false);
      setProgress(0);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const accept = limits.allowedMimeTypes.join(',');

  return (
    <div>
      {previews.length > 0 && (
        <S.PendingFiles>
          {previews.map(({ file, url }, i) => (
            <S.PendingFile key={`${file.name}-${i}`} title={`${file.name} · ${formatBytes(file.size)}`}>
              {file.type.startsWith('video/') ? (
                <video src={url} muted />
              ) : (
                <img src={url} alt={file.name} />
              )}
              {!sending && (
                <S.RemoveFile
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                >
                  ×
                </S.RemoveFile>
              )}
            </S.PendingFile>
          ))}
        </S.PendingFiles>
      )}
      <S.ComposerRow>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          hidden
          onChange={onPick}
          data-testid="support-file-input"
        />
        <Tooltip title="Attach image or video">
          <Button
            icon={<PaperClipOutlined />}
            onClick={openPicker}
            disabled={disabled || sending}
            aria-label="Attach image or video"
          />
        </Tooltip>
        <Input.TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={placeholder ?? 'Type a message…'}
          autoSize={{ minRows: 1, maxRows: 5 }}
          disabled={disabled || sending}
          autoFocus={autoFocus}
          maxLength={5000}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={send}
          disabled={!canSend}
          loading={sending}
          aria-label="Send"
        />
      </S.ComposerRow>
      {sending && files.length > 0 && (
        <Progress percent={progress} size="small" showInfo={false} style={{ marginTop: 6 }} />
      )}
      <S.Hint>
        Enter to send · Shift+Enter for a new line · drag, paste or attach up to{' '}
        {limits.maxFilesPerMessage} files (images {Math.round(limits.maxImageBytes / 1024 / 1024)} MB,
        videos {Math.round(limits.maxVideoBytes / 1024 / 1024)} MB)
      </S.Hint>
    </div>
  );
};

export default Composer;
